import { getCurrentUser } from "@/helpers/helper";
import { playNotificationSound } from "@/helpers/notificationSound";
import { LOGIN_FAILURE, LOGIN_SUCCESS, LOGOUT_USER } from "@/store/_mutationtypes";
import getEnv from "@/config/env";
import vuetify from "./vuetify";

let socketRef = null;
let reconnectAttemptCount = 0;
let reconnectTimer = null;
/** While logged out the socket must neither connect nor auto-reconnect. */
let isDisconnected = false;
let storeRef = null;

const localListeners = {
  open: new Set(),
  message: new Set(),
  close: new Set(),
  error: new Set(),
  /** { broadcastId, projectId } — listeners refetch the list */
  broadcastListRefresh: new Set(),
};

let lastPayload = null;

function safeStringify(obj) {
  try {
    if (obj && typeof obj === "object") {
      if (obj instanceof Error || obj.stack || obj.message) {
        return obj.stack || obj.message || String(obj);
      }
    }

    const seen = new WeakSet();
    return JSON.stringify(obj, function (key, value) {
      if (typeof value === "bigint") return value.toString() + "n";
      if (typeof value === "function") return `[Function: ${value.name || "anonymous"}]`;
      if (typeof value === "symbol") return value.toString();

      if (value && typeof value === "object") {
        if (seen.has(value)) return "[Circular]";
        seen.add(value);
      }
      return value;
    });
  } catch (err) {
    try { return String(obj); } catch { return "[unserializable payload]"; }
  }
}

function getAccessTokenStatus() {
  const user = getCurrentUser();

  if (!user?.access_token) {
    return { expired: true, token: null };
  }

  const expired = user?.expires_at
    ? new Date(user.expires_at).getTime() <= Date.now()
    : true;

  return { expired, token: user.access_token };
}

function buildUrl() {
  const { expired, token } = getAccessTokenStatus();
  if (expired || !token) return null;

  const baseUrl = getEnv("VUE_APP_STREAM_WS");
  return `${baseUrl}?auth_token=${encodeURIComponent(token)}&events=console-notification`;
}

async function parseIncomingMessage(data) {
  if (typeof data === "string") {
    try {
      return { parsed: JSON.parse(data), raw: data, text: data, kind: "json-string" };
    } catch {
      return { parsed: null, raw: data, text: data, kind: "text-string" };
    }
  }

  if (typeof Blob !== "undefined" && data instanceof Blob) {
    const type = data.type || "";
    if (type.includes("application/json") || type.includes("text/")) {
      try {
        const txt = await data.text();
        try {
          return { parsed: JSON.parse(txt), raw: data, text: txt, kind: "json-blob" };
        } catch {
          return { parsed: null, raw: data, text: txt, kind: "text-blob" };
        }
      } catch (err) {
        return { parsed: null, raw: data, kind: "blob", error: err };
      }
    }
    const objectUrl = URL.createObjectURL(data);
    return { parsed: null, raw: data, blobUrl: objectUrl, blobType: type, kind: "blob" };
  }

  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
    const ab = data instanceof ArrayBuffer ? data : data.buffer;
    try {
      const decoder = new TextDecoder();
      const txt = decoder.decode(ab);
      try {
        return { parsed: JSON.parse(txt), raw: data, text: txt, kind: "json-arraybuffer" };
      } catch {
        return { parsed: null, raw: data, text: txt, kind: "text-arraybuffer" };
      }
    } catch (err) {
      return { parsed: null, raw: data, kind: "arraybuffer", error: err };
    }
  }

  try {
    const s = String(data);
    try {
      return { parsed: JSON.parse(s), raw: data, text: s, kind: "json-fallback" };
    } catch {
      return { parsed: null, raw: data, text: s, kind: "text-fallback" };
    }
  } catch {
    return { parsed: null, raw: data, kind: "unknown" };
  }
}

function emitLocalEvent(eventName, payload) {
  if (eventName === "message") lastPayload = payload;
  const set = localListeners[eventName];
  if (!set) return;
  set.forEach((fn) => {
    try { fn(payload); } catch (_) { /* ignore */ }
  });
}

/** Walk nested JSON once; find broadcast_id without matching unrelated root `id`. */
function findBroadcastMeta(root) {
  if (!root || typeof root !== "object") return null;
  const stack = [root];
  const seen = new Set();
  while (stack.length) {
    const o = stack.pop();
    if (!o || typeof o !== "object" || seen.has(o)) continue;
    seen.add(o);
    const nested = o.broadcast && typeof o.broadcast === "object" ? o.broadcast : null;
    const broadcastId =
      o.broadcast_id ??
      o.broadcastId ??
      o.broadcast_uuid ??
      (nested ? nested.id ?? nested.broadcast_id : undefined);
    if (broadcastId) {
      return {
        broadcastId,
        projectId: o.project_id ?? o.projectId ?? o.project?.id ?? nested?.project_id,
      };
    }
    [
      o.data,
      o.body,
      o.detail,
      o.detail?.response,
      o.payload,
      o.message,
      o.notification,
      nested,
    ].forEach((c) => {
      if (c && typeof c === "object") stack.push(c);
    });
  }
  return null;
}

export default {
  install(Vue, { store } = {}) {
    storeRef = store || null;

    if (storeRef) {
      // A dead session must tear the socket down; a new one reconnects it.
      storeRef.subscribe(({ type }) => {
        if (type === `account/${LOGOUT_USER}` || type === `account/${LOGIN_FAILURE}`) {
          disconnect();
        } else if (type === `account/${LOGIN_SUCCESS}`) {
          reconnect();
        }
      });
    }

    function connect() {
      if (isDisconnected) return;
      if (socketRef && (socketRef.readyState === WebSocket.OPEN || socketRef.readyState === WebSocket.CONNECTING)) {
        return;
      }

      const url = buildUrl();
      if (!url) {
        // No usable token: stay idle and let the account store reconnect us
        // once a login or token refresh restores the session.
        return;
      }

      socketRef = new WebSocket(url);

      socketRef.addEventListener("open", () => {
        reconnectAttemptCount = 0;
        emitLocalEvent("open", { status: "open" });
      });

      socketRef.addEventListener("message", async (e) => {
        const result = await parseIncomingMessage(e.data);
        if(result?.parsed?.type === "heartbeat"){
          // ping message to check connection and ignore normal flow
          return;
        }
        const parsedRoot = result.parsed;
        const envelope = parsedRoot?.data ?? parsedRoot;
        const messageSource =
          parsedRoot && typeof parsedRoot === "object" ? parsedRoot : envelope;

        const meta =
          findBroadcastMeta(parsedRoot) ?? findBroadcastMeta(envelope);

        if (meta?.broadcastId && storeRef) {
          const projectId =
            meta.projectId ?? storeRef.state.project?.selectedProject?.id ?? null;
          emitLocalEvent("broadcastListRefresh", {
            broadcastId: meta.broadcastId,
            projectId,
          });
          return;
        }
        let message;
        let color = "info";

        if (parsedRoot || envelope) {
          message =
            messageSource?.message ||
            messageSource?.title ||
            envelope?.message ||
            envelope?.title ||
            safeStringify(envelope ?? messageSource);

          const firstModerationAlert =
            messageSource?.body?.detection_results?.[0] ??
            envelope?.body?.detection_results?.[0];

          if (firstModerationAlert?.severity) {
            const alertSeverity = firstModerationAlert.severity.toLowerCase();

            if (alertSeverity === "low") {
              color = "info";
            } else if (alertSeverity === "medium") {
              color = "warning";
            } else if (alertSeverity === "high") {
              color = "error";
            }
          }

        } else if (result.text) {
          message = result.text;
        } else if (result.blobUrl) {
          message = `[binary ${result.blobType || "data"} received]`;
        } else {
          message = "[binary data received]";
        }

        if (storeRef) {
          storeRef.commit('notification/SET_SUPPRESS_BADGE', false);
          await storeRef.dispatch('notification/LOAD_LAST_SEEN');

          try {
            storeRef.dispatch(`alert/ADD_ALERT`, { message, color }, { root: true });
          } catch (e) {
            const errorMessage =  e?.message || vuetify.framework.lang.t('$vuetify.common.unknown_err_msg_txt');
            storeRef.dispatch(`alert/ADD_ALERT`, { message: errorMessage, color: "error" }, { root: true });
          }

          const user = storeRef.state.account.organization || {};
          if (user.notification_sound_enabled && (parsedRoot || envelope)) {
            playNotificationSound(user.notification_ringtone);
          }
        }

        const payload = {
          raw: result.raw,
          parsed: result.parsed,
          message,
          color,
          kind: result.kind,
          blobUrl: result.blobUrl,
          blobType: result.blobType,
          text: result.text,
        };
        emitLocalEvent("message", payload);
      });

      socketRef.addEventListener("close", (ev) => {
        const payload = { status: "close", code: ev?.code };
        emitLocalEvent("close", payload);
        if (!isDisconnected) scheduleReconnect();
      });

      socketRef.addEventListener("error", (err) => {
        const payload = { status: "error", error: err };
        emitLocalEvent("error", payload);

        // if (storeRef) {
        //   try {
        //     storeRef.dispatch('alert/ADD_ALERT', { message: vuetify.framework.lang.t('$vuetify.notifications.connection_error'), color: "error" }, { root: true });
        //   } catch (e) {
        //     const errorMessage = e?.message || vuetify.framework.lang.t('$vuetify.common.unknown_err_msg_txt');
        //     storeRef.dispatch('alert/ADD_ALERT', { message: errorMessage, color: "error" }, { root: true });
        //   }
        // }
      });
    }

    function scheduleReconnect() {
      if (isDisconnected || reconnectTimer) return;

      reconnectAttemptCount++;
      const delay = Math.min(15000, 1000 * 2 ** reconnectAttemptCount);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    }

    /** Close for good: no auto-reconnect until the next successful login. */
    function disconnect() {
      isDisconnected = true;
      reconnectAttemptCount = 0;

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }

      const socket = socketRef;
      socketRef = null;
      if (socket) {
        try {
          socket.close();
        } catch (_) {
          /* ignore */
        }
      }
    }

    function reconnect() {
      isDisconnected = false;
      connect();
    }

    Vue.prototype.$socket = {
      raw: () => socketRef,
      send: (obj) => {
        if (!socketRef || socketRef.readyState !== WebSocket.OPEN) return false;
        socketRef.send(typeof obj === "string" ? obj : JSON.stringify(obj));
        return true;
      },
      close: disconnect,
      disconnect,
      on: (event, fn) => {
        if (!localListeners[event]) return () => {};
        localListeners[event].add(fn);
        return () => localListeners[event].delete(fn);
      },
      off: (event, fn) => {
        if (!localListeners[event]) return;
        localListeners[event].delete(fn);
      },
      once: (event, fn) => {
        if (!localListeners[event]) return () => {};
        const wrapper = (p) => { try { fn(p); } catch (_) { /* ignore */ } finally { localListeners[event].delete(wrapper); } };
        localListeners[event].add(wrapper);
        return () => localListeners[event].delete(wrapper);
      },
      getLastPayload: () => lastPayload,
      setStore: (s) => { storeRef = s; },
      revokeBlobUrl: (url) => { try { if (url) URL.revokeObjectURL(url); } catch (_) { /* ignore */ } }
    };

    connect();
    window.addEventListener("beforeunload", () => disconnect());
  },
};
